import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { formatDate } from "../utils/dateUtils";
import {
	Container,
	Grid,
	Card,
	CardContent,
	Typography,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Box,
	IconButton,
	Chip,
	Menu,
	MenuItem,
	CircularProgress,
	Alert,
	List,
	ListItem,
	ListItemText,
	Collapse,
} from "@mui/material";
import {
	Add,
	MoreVert,
	ContentCopy,
	Edit,
	Delete,
	Archive,
	Folder as FolderIcon,
	Assessment,
	CheckCircle,
	Error as ErrorIcon,
	ExpandMore,
	ChevronRight,
	FolderOpen,
	Http,
} from "@mui/icons-material";
import {
	fetchProjects,
	createProject,
	updateProject,
	deleteProject,
	archiveProject,
	selectProject,
} from "../store/slices/projectSlice";
import { fetchFolders } from "../store/slices/folderSlice";
import { useSnackbar } from "notistack";
import CloneDialog from "../components/CloneDialog";
import OwnerBadge from "../components/OwnerBadge";
import { canModify } from "../utils/permissions";

const defaultColors = [
	"#3b82f6",
	"#10b981",
	"#f59e0b",
	"#ef4444",
	"#8b5cf6",
	"#06b6d4",
	"#ec4899",
	"#6366f1",
];

const plural = (n, word) => `${n || 0} ${word}${n === 1 ? "" : "s"}`;

// A project's folders of one kind (tests or APIs), collapsible; a folder opens its list
function FolderGroup({ label, folders, open, onToggle, onOpen, countLabel }) {
	if (folders.length === 0) return null;
	return (
		<Box>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					cursor: "pointer",
					"&:hover": { bgcolor: "action.hover" },
					px: 1,
					py: 0.5,
					borderRadius: 1,
				}}
				onClick={(e) => {
					e.stopPropagation();
					onToggle();
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<FolderOpen sx={{ fontSize: 16, color: "text.secondary" }} />
					<Typography variant="caption" fontWeight={600}>
						{label} ({folders.length})
					</Typography>
				</Box>
				{open ? <ExpandMore sx={{ fontSize: 18 }} /> : <ChevronRight sx={{ fontSize: 18 }} />}
			</Box>
			<Collapse in={open}>
				<List dense sx={{ pt: 0.5 }}>
					{folders.map((folder) => (
						<ListItem
							key={folder.id}
							sx={{ pl: 3, py: 0.25, cursor: "pointer", "&:hover": { bgcolor: "action.hover" }, borderRadius: 1 }}
							onClick={(e) => {
								e.stopPropagation();
								onOpen(folder);
							}}
						>
							<FolderIcon sx={{ fontSize: 14, mr: 1, color: "text.secondary" }} />
							<ListItemText
								primary={folder.name}
								secondary={plural(folder.testCount, countLabel)}
								primaryTypographyProps={{ variant: "caption", noWrap: true }}
								secondaryTypographyProps={{ variant: "caption", fontSize: "0.65rem" }}
							/>
						</ListItem>
					))}
				</List>
			</Collapse>
		</Box>
	);
}

function Projects() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { enqueueSnackbar } = useSnackbar();
	const { projects, loading, error } = useSelector((state) => state.projects);
	const { folders } = useSelector((state) => state.folders);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editMode, setEditMode] = useState(false);
	const [currentProject, setCurrentProject] = useState(null);
	const [formData, setFormData] = useState({
		name: "",
		description: "",
		color: "#3b82f6",
		icon: "folder",
		workspaceId: "default", // TODO: Get from current workspace
	});
	const [anchorEl, setAnchorEl] = useState(null);
	const [selectedProjectId, setSelectedProjectId] = useState(null);
	const [expandedProjects, setExpandedProjects] = useState({});
	const [cloneTarget, setCloneTarget] = useState(null);
	const selectedProject = projects.find((p) => p.id === selectedProjectId);

	useEffect(() => {
		dispatch(fetchProjects());
	}, [dispatch]);

	// Refresh projects when window gains focus (user comes back from editing tests)
	useEffect(() => {
		const handleFocus = () => {
			dispatch(fetchProjects());
		};
		window.addEventListener("focus", handleFocus);
		return () => window.removeEventListener("focus", handleFocus);
	}, [dispatch]);

	// Fetch folders and refresh stats for all projects
	useEffect(() => {
		const refreshProjectData = async () => {
			for (const project of projects) {
				dispatch(fetchFolders(project.id));
				// Counts and stats are recomputed by the owner (or an admin)
				if (project.canModify === false) continue;
				// Refresh folder counts for each project
				api.post(`/folders/refresh-all-counts?projectId=${project.id}`)
					.catch((err) =>
						console.error("Failed to refresh folder counts:", err),
					);
				// Refresh project stats (including lastExecutionAt)
				api.post(`/projects/${project.id}/stats/refresh`)
					.catch((err) =>
						console.error("Failed to refresh project stats:", err),
					);
			}
			// Re-fetch projects to get updated stats
			if (projects.length > 0) {
				setTimeout(() => dispatch(fetchProjects()), 500);
			}
		};
		refreshProjectData();
	}, [projects.length, dispatch]);

	// key: `${projectId}:tests` or `${projectId}:apis`
	const toggleProjectExpand = (key) => {
		setExpandedProjects((prev) => ({
			...prev,
			[key]: !prev[key],
		}));
	};

	const getProjectFolders = (projectId, kind) => {
		return folders.filter(
			(f) => f.projectId === projectId && (kind === "API" ? f.kind === "API" : f.kind !== "API"),
		);
	};

	const handleOpenDialog = (project = null) => {
		if (project) {
			setEditMode(true);
			setCurrentProject(project);
			setFormData({
				name: project.name,
				description: project.description || "",
				color: project.color || "#3b82f6",
				icon: project.icon || "folder",
				workspaceId: project.workspaceId,
			});
		} else {
			setEditMode(false);
			setCurrentProject(null);
			setFormData({
				name: "",
				description: "",
				color: "#3b82f6",
				icon: "folder",
				workspaceId: "default",
			});
		}
		setDialogOpen(true);
	};

	const handleCloseDialog = () => {
		setDialogOpen(false);
		setEditMode(false);
		setCurrentProject(null);
	};

	const handleSubmit = async () => {
		try {
			if (editMode && currentProject) {
				await dispatch(
					updateProject({ id: currentProject.id, project: formData }),
				).unwrap();
				enqueueSnackbar("Project updated successfully", {
					variant: "success",
				});
			} else {
				await dispatch(createProject(formData)).unwrap();
				enqueueSnackbar("Project created successfully", {
					variant: "success",
				});
			}
			handleCloseDialog();
		} catch (error) {
			const msg =
				typeof error === "string"
					? error
					: error.message || "Failed to save project";
			enqueueSnackbar(msg, { variant: "error" });
		}
	};

	const handleMenuOpen = (event, projectId) => {
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
		setSelectedProjectId(projectId);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
		setSelectedProjectId(null);
	};

	const handleEdit = () => {
		const project = projects.find((p) => p.id === selectedProjectId);
		if (project) {
			handleOpenDialog(project);
		}
		handleMenuClose();
	};

	const handleDelete = async () => {
		try {
			await dispatch(deleteProject(selectedProjectId)).unwrap();
			enqueueSnackbar("Project deleted successfully", {
				variant: "success",
			});
		} catch (error) {
			// error from rejectWithValue is the payload directly (string), not an object with .message
			const errorMessage =
				typeof error === "string"
					? error
					: error.message ||
						"Cannot delete project with existing tests";
			enqueueSnackbar(errorMessage, { variant: "error" });
		}
		handleMenuClose();
	};

	const handleArchive = async () => {
		try {
			await dispatch(archiveProject(selectedProjectId)).unwrap();
			enqueueSnackbar("Project archived successfully", {
				variant: "success",
			});
		} catch (error) {
			enqueueSnackbar(error.message || "Failed to archive project", {
				variant: "error",
			});
		}
		handleMenuClose();
	};

	// "/tests" (browser tests, the card's default) or "/apis"
	const handleCardClick = (project, list = "/tests") => {
		dispatch(selectProject(project));
		navigate(`${list}?project=${project.id}`);
	};

	if (loading && projects.length === 0) {
		return (
			<Container maxWidth="xl" sx={{ mt: 4, textAlign: "center" }}>
				<CircularProgress />
			</Container>
		);
	}

	return (
		<Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				mb={4}
			>
				<Box>
					<Typography variant="h4" gutterBottom>
						Projects
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Organize your tests by application or product
					</Typography>
				</Box>
				<Button
					variant="contained"
					startIcon={<Add />}
					onClick={() => handleOpenDialog()}
				>
					New Project
				</Button>
			</Box>

			{error && (
				<Alert severity="error" sx={{ mb: 3 }}>
					{error}
				</Alert>
			)}

			{projects.length === 0 ? (
				<Box
					sx={{
						textAlign: "center",
						py: 8,
						backgroundColor: "background.paper",
						borderRadius: 2,
					}}
				>
					<FolderIcon
						sx={{ fontSize: 80, color: "text.disabled", mb: 2 }}
					/>
					<Typography variant="h6" gutterBottom>
						No projects yet
					</Typography>
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ mb: 3 }}
					>
						Create your first project to organize tests
					</Typography>
					<Button
						variant="contained"
						startIcon={<Add />}
						onClick={() => handleOpenDialog()}
					>
						Create Project
					</Button>
				</Box>
			) : (
				<Grid container spacing={3}>
					{projects.map((project) => (
						<Grid item xs={12} sm={6} md={4} key={project.id}>
							<Card
								sx={{
									borderLeft: `4px solid ${project.color}`,
									cursor: "pointer",
									transition: "all 0.2s",
									"&:hover": {
										boxShadow: 6,
										transform: "translateY(-4px)",
									},
								}}
								onClick={() => handleCardClick(project)}
							>
								<CardContent>
									<Box
										display="flex"
										justifyContent="space-between"
										alignItems="flex-start"
									>
										<Box
											display="flex"
											alignItems="center"
											gap={1}
											flex={1}
										>
											<FolderIcon
												sx={{
													color: project.color,
													fontSize: 28,
												}}
											/>
											<Typography variant="h6" noWrap>
												{project.name}
											</Typography>
										</Box>
										<IconButton
											size="small"
											onClick={(e) =>
												handleMenuOpen(e, project.id)
											}
											aria-label={`Actions for ${project.name}`}
										>
											<MoreVert />
										</IconButton>
									</Box>
									<OwnerBadge item={project} sx={{ mb: 1 }} />

									<Typography
										variant="body2"
										color="text.secondary"
										sx={{ mt: 1, mb: 2, minHeight: 40 }}
									>
										{project.description ||
											"No description"}
									</Typography>

									<Box display="flex" flexWrap="wrap" gap={1}>
										<Chip
											icon={<Assessment />}
											label={plural(project.stats?.totalTests, "test")}
											size="small"
											onClick={(e) => {
												e.stopPropagation();
												handleCardClick(project, "/tests");
											}}
										/>
										<Chip
											icon={<Http />}
											label={plural(project.stats?.totalApis, "API")}
											size="small"
											onClick={(e) => {
												e.stopPropagation();
												handleCardClick(project, "/apis");
											}}
										/>
										{(project.stats?.totalTests > 0 || project.stats?.totalApis > 0) && (
											<Chip
												icon={
													project.stats.passRate >=
													80 ? (
														<CheckCircle />
													) : (
														<ErrorIcon />
													)
												}
												label={`${project.stats.passRate || 0}% pass`}
												size="small"
												color={
													project.stats.passRate >= 80
														? "success"
														: project.stats
																	.passRate >=
															  60
															? "warning"
															: "error"
												}
											/>
										)}
									</Box>

									{project.stats?.lastExecutionAt && (
										<Typography
											variant="caption"
											color="text.secondary"
											sx={{ mt: 2, display: "block" }}
										>
											Last run:{" "}
											{formatDate(project.stats.lastExecutionAt)}
										</Typography>
									)}

									{/* Folders: tests and APIs have their own */}
									{getProjectFolders(project.id).length + getProjectFolders(project.id, "API").length > 0 && (
										<Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
											<FolderGroup
												label="Test folders"
												countLabel="test"
												folders={getProjectFolders(project.id)}
												open={!!expandedProjects[`${project.id}:tests`]}
												onToggle={() => toggleProjectExpand(`${project.id}:tests`)}
												onOpen={(folder) => navigate(`/tests?project=${project.id}&folder=${folder.id}`)}
											/>
											<FolderGroup
												label="API folders"
												countLabel="API"
												folders={getProjectFolders(project.id, "API")}
												open={!!expandedProjects[`${project.id}:apis`]}
												onToggle={() => toggleProjectExpand(`${project.id}:apis`)}
												onOpen={(folder) => navigate(`/apis?project=${project.id}&folder=${folder.id}`)}
											/>
										</Box>
									)}
								</CardContent>
							</Card>
						</Grid>
					))}
				</Grid>
			)}

			{/* Context Menu */}
			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleMenuClose}
			>
				<MenuItem
					onClick={() => {
						const project = projects.find(
							(p) => p.id === selectedProjectId,
						);
						if (project) {
							handleCardClick(project);
						}
						handleMenuClose();
					}}
				>
					<FolderIcon sx={{ mr: 1, fontSize: 20 }} />
					View Tests
				</MenuItem>
				<MenuItem
					onClick={() => {
						const project = projects.find((p) => p.id === selectedProjectId);
						if (project) handleCardClick(project, "/apis");
						handleMenuClose();
					}}
				>
					<Http sx={{ mr: 1, fontSize: 20 }} />
					View APIs
				</MenuItem>
				<MenuItem
					onClick={() => {
						setCloneTarget(selectedProject);
						handleMenuClose();
					}}
				>
					<ContentCopy sx={{ mr: 1, fontSize: 20 }} />
					Clone
				</MenuItem>
				{/* Owner or admin only; others can view, run and clone */}
				{canModify(selectedProject) && [
					<MenuItem key="edit" onClick={handleEdit}>
						<Edit sx={{ mr: 1, fontSize: 20 }} />
						Edit
					</MenuItem>,
					<MenuItem key="archive" onClick={handleArchive}>
						<Archive sx={{ mr: 1, fontSize: 20 }} />
						Archive
					</MenuItem>,
					<MenuItem key="delete" onClick={handleDelete} sx={{ color: "error.main" }}>
						<Delete sx={{ mr: 1, fontSize: 20 }} />
						Delete
					</MenuItem>,
				]}
			</Menu>

			<CloneDialog
				open={!!cloneTarget}
				kind="project"
				item={cloneTarget}
				onClose={() => setCloneTarget(null)}
				onCloned={() => dispatch(fetchProjects())}
			/>

			{/* Create/Edit Dialog */}
			<Dialog
				open={dialogOpen}
				onClose={handleCloseDialog}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>
					{editMode ? "Edit Project" : "Create New Project"}
				</DialogTitle>
				<DialogContent>
					<Box sx={{ pt: 2 }}>
						<TextField
							fullWidth
							label="Project Name"
							value={formData.name}
							onChange={(e) =>
								setFormData({
									...formData,
									name: e.target.value,
								})
							}
							sx={{ mb: 2 }}
							required
						/>
						<TextField
							fullWidth
							label="Description"
							value={formData.description}
							onChange={(e) =>
								setFormData({
									...formData,
									description: e.target.value,
								})
							}
							multiline
							rows={3}
							sx={{ mb: 2 }}
						/>
						<Typography variant="subtitle2" gutterBottom>
							Color
						</Typography>
						<Box display="flex" gap={1} mb={2}>
							{defaultColors.map((color) => (
								<Box
									key={color}
									onClick={() =>
										setFormData({ ...formData, color })
									}
									sx={{
										width: 40,
										height: 40,
										backgroundColor: color,
										borderRadius: 1,
										cursor: "pointer",
										border:
											formData.color === color ? 3 : 1,
										borderColor:
											formData.color === color
												? "text.primary"
												: "divider",
										transition: "all 0.2s",
										"&:hover": {
											transform: "scale(1.1)",
										},
									}}
								/>
							))}
						</Box>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseDialog}>Cancel</Button>
					<Button
						onClick={handleSubmit}
						variant="contained"
						disabled={!formData.name.trim()}
					>
						{editMode ? "Update" : "Create"}
					</Button>
				</DialogActions>
			</Dialog>
		</Container>
	);
}

export default Projects;
